import { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import { useFormikContext, FastField, Field } from "formik";
import ClonePropertiespopup from "../../../../Components/ClonePropertiesPopup";
import {
  LabeledTextField,
  CustomOptions,
} from "../../../../HelperFunctions/EmployeeFormHelpers";
import { SALUTATIONS_ENUM, GENDER_OPTIONS } from "../../../../Enums/Enum";
import { Dropdown } from "../../../../HelperFunctions/PayrollDropdown";
import { enqueueSnackbar } from "notistack";
import employeeManagementStyles from "../../../../styles/employeeManagement.styles";

// --- Constants & Regex ---
const NAME_REGEX = /^[a-zA-Z0-9_()\s\-!',]*$/;
const PHONE_INPUT_REGEX = /^[0-9]*$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SSN_INPUT_REGEX = /[^0-9X\x2d]/i;

// --- Validation & Formatting Helpers ---
const formatSSN = (value, previousValue = "") => {
  let digitsOnly = value.replace(/\D/g, "");
  const oldDigits = previousValue.replace(/\D/g, "");

  if (value.length < previousValue.length && digitsOnly.length === oldDigits.length && oldDigits.length > 0) {
    digitsOnly = digitsOnly.slice(0, -1);
  }

  digitsOnly = digitsOnly.slice(0, 9);
  if (digitsOnly.length === 0) return "";

  const padded = digitsOnly.padEnd(9, "X");
  return `${padded.slice(0, 3)}-${padded.slice(3, 5)}-${padded.slice(5)}`;
};

const generatePrintedName = (firstName, middleName, lastName) => {
  const fn = (firstName || "").trim();
  const mn = (middleName || "").trim();
  const ln = (lastName || "").trim();

  let generatedName = fn;
  if (fn && (ln || mn)) {
    generatedName += ",";
  }
  if (ln) generatedName += " " + ln;
  if (mn) generatedName += " " + mn;
  return generatedName.trim();
};

const validatePhoneLength = (val) => {
  return (val && String(val).length > 0 && String(val).length < 10) ? "Mobile Number must be 10 digits" : null;
};

const validateEmailFormat = (val) => {
  return (val && String(val).length > 0 && !EMAIL_REGEX.test(val)) ? "Invalid Email Format" : null;
};

const validateSSNLength = (val) => {
  const strVal = String(val || "").trim();
  if (!strVal) return null; // Not required, so no error if empty
  const actualDigits = strVal.replace(/X/g, "").replace(/\D/g, "");
  // Error only if it's not a masked value from edit mode and has less than 9 digits
  return (!strVal.includes("X") && actualDigits.length > 0 && actualDigits.length < 9) ? "SSN must be 9 digits" : null;
};

// --- Subcomponents ---
const ProfileTextField = memo(({ name, label, placeholder, required = false, onChange, onBlur, inputProps }) => (
  <FastField name={name}>
    {({ field }) => (
      <LabeledTextField
        {...field}
        label={label}
        required={required}
        placeholder={placeholder}
        onChange={onChange || field.onChange}
        onBlur={onBlur || field.onBlur}
        inputProps={inputProps}
      />
    )}
  </FastField>
));

const EmployeeProfile = ({
  allCorporations = [],
  cloneCorps,
  setCloneCorps,
  setIsCloning,
  employeeId,
  loading,
  corporationId,
}) => {
  const {
    values,
    setFieldValue,
    handleBlur,
    setFieldError,
    setFieldTouched,
  } = useFormikContext();
  const [openClonePopup, setOpenClonePopup] = useState(false);
  const isManuallyEditedRef = useRef(false);
  const prevGeneratedName = useRef(
    generatePrintedName(values.firstName, values.middleName, values.lastName)
  );
  const prevFirstName = useRef((values.firstName || "").trim());
  const prevMiddleName = useRef((values.middleName || "").trim());
  const prevLastName = useRef((values.lastName || "").trim());
  
  // Keep SSN value safely detached from function recreation cycle
  const ssnRef = useRef(values.ssn);
  useEffect(() => {
    ssnRef.current = values.ssn;
  }, [values.ssn]);

  const corporationsList = useMemo(() => [
    { corpName: "--Select--", corpValue: "000000000000000000000000000000000000" },
    ...(allCorporations || []),
  ], [allCorporations]);

  // If initial value in edit mode or add mode was already different from generated name, mark as manually edited
  useEffect(() => {
    const initGen = generatePrintedName(values.firstName, values.middleName, values.lastName);
    if (values.checksToBePrintedAs && values.checksToBePrintedAs !== initGen) {
      isManuallyEditedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (employeeId) return; // Skip for existing employees

    const currentFirst = (values.firstName || "").trim();
    const currentMiddle = (values.middleName || "").trim();
    const currentLast = (values.lastName || "").trim();

    const isNameChanged =
      currentFirst !== prevFirstName.current ||
      currentMiddle !== prevMiddleName.current ||
      currentLast !== prevLastName.current;

    prevFirstName.current = currentFirst;
    prevMiddleName.current = currentMiddle;
    prevLastName.current = currentLast;

    // ONLY auto-generate when firstName, middleName, or lastName actually changed
    // AND the user hasn't manually customized the checksToBePrintedAs field
    if (isNameChanged && !isManuallyEditedRef.current) {
      const generatedName = generatePrintedName(currentFirst, currentMiddle, currentLast);
      setFieldValue("checksToBePrintedAs", generatedName);
      prevGeneratedName.current = generatedName;
    }
  }, [values.firstName, values.middleName, values.lastName, employeeId, setFieldValue]);

  const handleCloneOptionClick = useCallback(() => {
    if (values.addToAllCorporations) {
      setFieldValue("addToAllCorporations", false);
      if (setIsCloning) setIsCloning(false);
      if (setCloneCorps) setCloneCorps([]);
    } else {
      const availableCorps = allCorporations
        .filter((c) => c.corpValue !== values.corporation)
        .map((c) => c.corpValue);

      if (setCloneCorps) setCloneCorps(availableCorps);
      if (setIsCloning) setIsCloning(true);
      setFieldValue("addToAllCorporations", true);
    }
  }, [allCorporations, values.addToAllCorporations, values.corporation, setFieldValue, setIsCloning, setCloneCorps]);

  const handleCloneConfirm = useCallback((selectedCorps) => {
    if (selectedCorps && selectedCorps.length > 0) {
      if (setCloneCorps) setCloneCorps(selectedCorps);
      if (setIsCloning) setIsCloning(true);
      setFieldValue("addToAllCorporations", true);
    } else {
      if (setCloneCorps) setCloneCorps([]);
      if (setIsCloning) setIsCloning(false);
      setFieldValue("addToAllCorporations", false);
    }
    setOpenClonePopup(false);
  }, [setCloneCorps, setIsCloning, setFieldValue]);

  // --- Handlers ---
  const handleNameChange = useCallback((e) => {
    const fieldName = e.target.name;
    const val = e?.target?.value !== undefined ? e.target.value : e;
    if (NAME_REGEX.test(val)) {
      setFieldValue(fieldName, val);
    } else {
      enqueueSnackbar("Name fields do not support some special characters.", {
        variant: "error",
      });
    }
  }, [setFieldValue]);

  const handleMobileChange = useCallback((e) => {
    const val = e?.target?.value !== undefined ? e.target.value : e;
    if (PHONE_INPUT_REGEX.test(val)) {
      setFieldValue("mobileNumber", val);
    } else {
      enqueueSnackbar("Mobile Number must be numeric.", {
        variant: "error",
      });
    }
  }, [setFieldValue]);

  const handleMobileBlur = useCallback((e) => {
    handleBlur(e);
    const val = e?.target?.value !== undefined ? e.target.value : e;
    const error = validatePhoneLength(val);
    
    if (error) {
      setFieldValue("mobileNumber", "");
      setFieldTouched("mobileNumber", true, false);
      enqueueSnackbar(error, { variant: "error" });
    } else {
      setFieldError("mobileNumber", undefined);
    }
  }, [handleBlur, setFieldValue, setFieldTouched, setFieldError]);

  const handleEmailBlur = useCallback((e) => {
    handleBlur(e);
    const val = e?.target?.value !== undefined ? e.target.value : e;
    const error = validateEmailFormat(val);
    
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
    } else {
      setFieldError("emailId", undefined);
    }
  }, [handleBlur, setFieldError]);

  const handleSSNChange = useCallback((e) => {
    const val = e?.target?.value !== undefined ? e.target.value : e;
    if (SSN_INPUT_REGEX.test(val)) {
      enqueueSnackbar("SSN must be numeric.", { variant: "error" });
      return;
    }
    const formatted = formatSSN(val, ssnRef.current);
    setFieldValue("ssn", formatted);
  }, [setFieldValue]);

  const handleSSNBlur = useCallback((e) => {
    handleBlur(e);

    const val = e?.target?.value !== undefined ? e.target.value : (typeof e === "string" ? e : "");
    const error = validateSSNLength(val);
    
    if (error) {
      setFieldTouched("ssn", true, false);
      enqueueSnackbar(error, { variant: "error" });
    }
  }, [handleBlur, setFieldTouched]);

  const handleChecksToBePrintedAsChange = useCallback((e) => {
    isManuallyEditedRef.current = true;
    const val = e?.target?.value !== undefined ? e.target.value : e;
    setFieldValue("checksToBePrintedAs", val);
  }, [setFieldValue]);

  const filteredCloneCorps = useMemo(() => {
    return allCorporations.filter((c) => c.corpValue !== values.corporation);
  }, [allCorporations, values.corporation]);

  return (
      <Stack
        height="100%"
        overflow={"auto"}
        pl={1.5}
        sx={employeeManagementStyles.profileContainer}
      >
        <Stack p={"17px"} bgcolor={"#ebf3f9"} borderRadius={5} gap={"10px"}>
          <Typography
            fontSize={"1rem !important"}
            fontWeight={600}
            color="text.secondary"
            alignSelf={"center"}
          >
            Employee Profile
          </Typography>
          <Field name="corporation">
            {({ field, form, meta }) => (
              <Box>
                <Typography color="text.secondary" fontSize={11} fontWeight="bold" mb={0.5}>
                  Corporation <span style={{ color: "red" }}>*</span>
                </Typography>
                <Autocomplete
                  options={corporationsList}
                  size="small"
                  disableClearable
                  autoHighlight
                  loading={loading}
                  disabled={!!employeeId || !!corporationId}
                  componentsProps={{ popper: { placement: "top-start" } }}
                  ListboxProps={{ sx: employeeManagementStyles.autocompleteListbox }}
                  getOptionLabel={(option) => option.corpName || ""}
                  isOptionEqualToValue={(option, value) => String(option.corpValue).toLowerCase() === String(value.corpValue).toLowerCase()}
                  value={corporationsList.find((corp) => String(corp.corpValue).toLowerCase() === String(field.value).toLowerCase()) || (field.value ? { corpValue: field.value, corpName: values.corporationName || "" } : corporationsList[0])}
                  onChange={(event, newValue) => {
                    form.setFieldValue("corporation", newValue.corpValue);
                  }}
                  onBlur={field.onBlur}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="corporation"
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                      sx={employeeManagementStyles.getAutocompleteInputSx(!!employeeId || !!corporationId)}
                      variant="filled"
                    />
                  )}
                />
              </Box>
            )}
            </Field>

          <FastField name="salutation">
            {({ field,form }) => (
              <Box>
                <Typography color="text.secondary" fontSize={11} fontWeight="bold" mb={0.5}>
                  Salutation
                </Typography>
                  <Dropdown
                    name={field.name}
                    value={SALUTATIONS_ENUM.find(s => s.id === field.value) || { label: "--Select--", id: "" }}
                    options={[{ label: "--Select--", id: "" }, ...SALUTATIONS_ENUM]}
                    onChange={(name, newValue) => {
                      form.setFieldValue(name, newValue?.id ?? "");
                    }}
                    getOptionLabel={(option) => option.label}
                    isEqual={(option, value) => option.id === value.id}
                    sx={employeeManagementStyles.compactDropdownSx}
                    onBlur={field.onBlur}
                  />
              </Box>
            )}
          </FastField>

          <ProfileTextField 
            name="firstName" 
            label="First Name" 
            placeholder="Enter First Name" 
            required 
            onChange={handleNameChange} 
          />
          
          <ProfileTextField 
            name="middleName" 
            label="Middle Name" 
            placeholder="Enter Middle Name" 
            onChange={handleNameChange} 
          />
          
          <ProfileTextField 
            name="lastName" 
            label="Last Name" 
            placeholder="Enter Last Name" 
            onChange={handleNameChange} 
          />
          
          <FastField name="gender">
            {({ field, form }) => (
              <Box>
                <Typography color="text.secondary" fontSize={11} fontWeight="bold" mb={0.5}>
                  Gender
                </Typography>
                <Dropdown
                  name={field.name}
                  value={GENDER_OPTIONS.find(g => g.id === field.value) || { label: "--Select--", id: "" }}
                  options={[{ label: "--Select--", id: "" }, ...GENDER_OPTIONS]}
                  onChange={(name, newValue) => {
                    form.setFieldValue(name, newValue?.id ?? "");
                  }}
                  getOptionLabel={(option) => option.label}
                  isEqual={(option, value) => option.id === value.id}
                  sx={employeeManagementStyles.compactDropdownSx}
                  onBlur={field.onBlur}
                />
              </Box>
            )}
          </FastField>

          <ProfileTextField 
            name="mobileNumber" 
            label="Mobile Number" 
            placeholder="Enter Mobile Number" 
            inputProps={{ maxLength: 10 }} 
            onChange={handleMobileChange} 
            onBlur={handleMobileBlur} 
          />

          <ProfileTextField 
            name="emailId" 
            label="Email ID" 
            placeholder="Enter Email ID" 
            onBlur={handleEmailBlur} 
          />

          <ProfileTextField 
            name="ssn" 
            label="SSN" 
            placeholder="XXX-XX-XXXX" 
            onChange={handleSSNChange} 
            onBlur={handleSSNBlur} 
          />

          <ProfileTextField 
            name="checksToBePrintedAs" 
            label="Checks to be printed as" 
            placeholder="Enter Checks to be printed as" 
            onChange={handleChecksToBePrintedAsChange}
          />

          <CustomOptions
            name="addToAllCorporations"
            label="Add this employee to all corporations"
            onClick={handleCloneOptionClick}
          />
          {values.addToAllCorporations && (
            <Typography
              variant="caption"
              sx={employeeManagementStyles.cloneLinkText}
              onClick={() => setOpenClonePopup(true)}
            >
              {`for ${cloneCorps.length}  ${cloneCorps.length === 1 ? "property" : "properties"}`}
            </Typography>
          )}
          <CustomOptions name="active" label="Active" />
          <ClonePropertiespopup
            open={openClonePopup}
            onClose={() => setOpenClonePopup(false)}
            onSelect={handleCloneConfirm}
            corporations={filteredCloneCorps}
            sourceCorporationId={values.corporation}
            selectedCorpIds={cloneCorps}
          />
        </Stack>
        
      </Stack>

  );
};

export default EmployeeProfile;
